import { ShieldAlert, ShieldCheck } from 'lucide-react';
import { trpc } from '../lib/trpc.ts';
import { Badge } from '../components/Badge.tsx';
import { EmptyState } from '../components/EmptyState.tsx';
import { Reveal } from '../components/motion.tsx';
import './Zones.css';

export function Zones() {
  const partitions = trpc.zone.listPartitions.useQuery();
  const zones = trpc.zone.listZones.useQuery();
  const utils = trpc.useUtils();
  const arm = trpc.zone.arm.useMutation({
    onSuccess: () => utils.zone.listPartitions.invalidate(),
  });
  const disarm = trpc.zone.disarm.useMutation({
    onSuccess: () => utils.zone.listPartitions.invalidate(),
  });
  const bypass = trpc.zone.bypass.useMutation({
    onSuccess: () => utils.zone.listZones.invalidate(),
  });
  const unbypass = trpc.zone.unbypass.useMutation({
    onSuccess: () => utils.zone.listZones.invalidate(),
  });

  return (
    <div className="stack zones-page">
      <Reveal>
        <header className="page-head">
          <span className="section-eyebrow">Intrusion · Arming</span>
          <h1>
            Zones &amp; <span className="serif-accent">partitions</span>
          </h1>
          <p className="muted">
            Arm or disarm partitions and bypass individual inputs on the A22K.
          </p>
        </header>
      </Reveal>

      <Reveal delay={0.05}>
        <div className="card">
          <div className="card-header">
            <ShieldCheck size={14} />
            <h2>Partitions</h2>
          </div>
          {!partitions.data || partitions.data.length === 0 ? (
            <EmptyState
              icon={ShieldCheck}
              title="No partitions"
              body="Partitions are arming groups configured in the controller. They appear here after the relay syncs."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>State</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {partitions.data.map((p) => (
                  <tr key={p.id}>
                    <td className="cell-name">{p.name}</td>
                    <td>
                      <Badge tone={p.state === 'disarmed' ? 'ok' : 'warn'}>{p.state}</Badge>
                    </td>
                    <td className="row" style={{ justifyContent: 'flex-end' }}>
                      {p.state === 'disarmed' ? (
                        <button onClick={() => void arm.mutate({ id: p.id, mode: 'away' })}>
                          Arm away
                        </button>
                      ) : (
                        <button
                          className="secondary"
                          onClick={() => void disarm.mutate({ id: p.id })}
                        >
                          Disarm
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="card">
          <div className="card-header">
            <ShieldAlert size={14} />
            <h2>Zones</h2>
          </div>
          {!zones.data || zones.data.length === 0 ? (
            <EmptyState
              icon={ShieldAlert}
              title="No zones yet"
              body="Zones are the multi-purpose inputs on the A22K (up to 6, or 12 with zone-doubling)."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Input</th>
                  <th>State</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {zones.data.map((z) => (
                  <tr key={z.id}>
                    <td className="cell-name">{z.name}</td>
                    <td className="mono dim">{z.inputNumber ?? '—'}</td>
                    <td>
                      <Badge
                        tone={
                          z.state === 'normal'
                            ? 'ok'
                            : z.state === 'bypassed'
                              ? 'warn'
                              : z.state === 'alarm'
                                ? 'danger'
                                : 'warn'
                        }
                      >
                        {z.state}
                      </Badge>
                    </td>
                    <td className="row" style={{ justifyContent: 'flex-end' }}>
                      {z.state === 'bypassed' ? (
                        <button
                          className="secondary"
                          onClick={() => void unbypass.mutate({ id: z.id })}
                        >
                          Un-bypass
                        </button>
                      ) : (
                        <button
                          className="secondary"
                          onClick={() => void bypass.mutate({ id: z.id })}
                        >
                          Bypass
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Reveal>
    </div>
  );
}
